import chalk from "chalk";
import {
  cpSync,
  existsSync,
  mkdirSync,
  readFileSync,
  readdirSync,
  rmSync,
  writeFileSync,
} from "node:fs";
import { dirname, join, resolve } from "node:path";
import { fileURLToPath } from "node:url";
import { spawnSync } from "node:child_process";
import { ensureMcpRegistered, ensureCaiHooks, ensureLearnHook } from "./mcp/install.js";
import { buildPredriftIndex } from "./mcp/predrift-index.js";
import { generateRules } from "./rules.js";
import { enableLearn, isLearnEnabled } from "./learn/recorder.js";
import { MANIFEST_FILES } from "./scanner/manifest.js";

// Must stay in sync with the "files" field in package.json
const BOOTSTRAP_ENTRIES = [
  ".tool-configs",
  "AGENTS.md",
  "CLAUDE.md",
  "LICENSE",
  "README.md",
  "ROUTER.md",
  "SETUP.md",
  "SYNC.md",
  "context",
  "dist",
  "mascot",
  "package.json",
  "patterns",
  "screenshots",
  "setup.sh",
  "sync.sh",
  "tsup.config.ts",
  "update.sh",
  "visualize.sh",
] as const;

export interface BootstrapResult {
  targetDir: string;
  copied: string[];
  dryRun?: boolean;
  minimal?: boolean;
  setupRan?: boolean;
  mcpStatus?: "registered" | "already_registered" | "claude_not_found" | "failed";
  mcpSkipped?: boolean;
  rulesGenerated?: number;
  rulesSkipped?: boolean;
  skillsGenerated?: number;
  skillsSkipped?: boolean;
  hooksInstalled?: number;
  hooksSkipped?: boolean;
  /** True when correction recording was just activated (vs. already on). */
  learnEnabled?: boolean;
  learnSkipped?: boolean;
  /** Relative path to sub-project root if project manifests are not in the repo root. */
  detectedSubRoot?: string;
}

export function runBootstrap(opts: {
  targetDir?: string;
  sourceRoot?: string;
  force?: boolean;
  dryRun?: boolean;
  minimal?: boolean;
  installMcp?: boolean;
  installHooks?: boolean;
  enableLearn?: boolean;
  generateRules?: boolean;
  generateSkills?: boolean;
} = {}): BootstrapResult {
  const sourceRoot = opts.sourceRoot ?? getPackageRoot();
  const projectRoot = resolve(opts.targetDir ?? process.cwd());
  const destination = join(projectRoot, ".cai");
  const minimal = Boolean(opts.minimal);
  const installMcp = !minimal && opts.installMcp !== false;
  const installHooks = !minimal && opts.installHooks !== false;
  const enableLearnByDefault = !minimal && installHooks && opts.enableLearn !== false;
  const shouldGenerateRules = !minimal && opts.generateRules !== false;
  const shouldGenerateSkills = !minimal && opts.generateSkills !== false;

  if (sourceRoot === destination) {
    throw new Error("Refusing to bootstrap CAI into itself.");
  }

  if (existsSync(destination)) {
    const hasContent = readdirSync(destination).length > 0;
    if (hasContent && !opts.force) {
      throw new Error(`.cai already exists in ${projectRoot}. Use --force to replace it.`);
    }
    if (!opts.dryRun) {
      rmSync(destination, { recursive: true, force: true });
    }
  }

  const copied: string[] = [];
  for (const entry of BOOTSTRAP_ENTRIES) {
    const from = join(sourceRoot, entry);
    if (!existsSync(from)) continue;
    copied.push(entry);
  }

  if (opts.dryRun) {
    return {
      targetDir: destination,
      copied,
      dryRun: true,
      minimal,
      mcpSkipped: !installMcp,
      rulesSkipped: !shouldGenerateRules,
      skillsSkipped: !shouldGenerateSkills,
      hooksSkipped: !installHooks,
      learnSkipped: !enableLearnByDefault,
      detectedSubRoot: detectSubProjectRoot(projectRoot),
    };
  }

  mkdirSync(destination, { recursive: true });

  const written: string[] = [];
  for (const entry of copied) {
    const from = join(sourceRoot, entry);
    const to = join(destination, entry);
    cpSync(from, to, { recursive: true });
    written.push(entry);
  }

  // Strip HTML template comments from scaffold markdown files.
  // These comments guide humans during initial population but waste tokens
  // when Claude reads them in every session (~2,000-2,500 tokens total).
  stripTemplateComments(destination);

  // Build the predrift index so it's ready if the user opts in later
  try { buildPredriftIndex(destination); } catch { /* non-critical */ }

  const mcp = installMcp ? ensureMcpRegistered(projectRoot) : undefined;
  const hooksInstalled = installHooks ? ensureCaiHooks(projectRoot) : 0;

  // Enable correction recording by default + install the UserPromptSubmit hook.
  // The recorder is local-only, gitignored, and never uploaded — opting in by
  // default makes the AI→You feedback loop work without requiring a separate step.
  // Run `cai learn forget` to wipe everything if you change your mind.
  const learnWasOff = enableLearnByDefault && !isLearnEnabled(projectRoot);
  if (learnWasOff && enableLearnByDefault) {
    enableLearn(projectRoot);
    ensureLearnHook(projectRoot);
  }

  // Generate .claude/rules/ from scaffold context
  let rulesGenerated = 0;
  if (shouldGenerateRules) {
    try {
      const config = { projectRoot, scaffoldRoot: destination, subProjectRoot: projectRoot, settings: {} };
      const rules = generateRules(config);
      rulesGenerated = rules.written.length;
    } catch {
      // Non-fatal: rules generation can fail on first setup before content exists
    }
  }

  // Generate .claude/skills/ for common CAI workflows
  const skillsGenerated = shouldGenerateSkills ? generateSkills(projectRoot) : 0;

  const detectedSubRoot = detectSubProjectRoot(projectRoot);

  return {
    targetDir: destination,
    copied: written,
    minimal,
    mcpStatus: mcp?.status,
    mcpSkipped: !installMcp,
    rulesGenerated,
    rulesSkipped: !shouldGenerateRules,
    skillsGenerated,
    skillsSkipped: !shouldGenerateSkills,
    hooksInstalled,
    hooksSkipped: !installHooks,
    learnEnabled: learnWasOff,
    learnSkipped: !enableLearnByDefault,
    detectedSubRoot,
  };
}

export function printBootstrapResult(result: BootstrapResult): void {
  const b = chalk.bold;
  const dim = chalk.dim;

  console.log();
  console.log(`  ${b("cai setup")} ${dim(result.dryRun ? "· dry run" : result.minimal ? "· minimal scaffold installed" : "· scaffold installed")}`);
  console.log();
  console.log(`${result.dryRun ? chalk.cyan("  →") : chalk.green("  ✓")} ${result.targetDir}`);
  console.log(dim(`  ${result.copied.length} entries ${result.dryRun ? "would be written" : "written"}`));
  console.log();

  if (result.dryRun) {
    console.log(chalk.bold("  CAI would create:"));
    console.log(dim(`    .cai/ (${result.copied.length} package scaffold entries)`));
    if (!result.skillsSkipped) console.log(dim("    .claude/skills/cai-check/SKILL.md"));
    if (!result.skillsSkipped) console.log(dim("    .claude/skills/cai-sync/SKILL.md"));
    if (!result.rulesSkipped) console.log(dim("    .claude/rules/*.md (generated path-scoped rules, when context supports it)"));
    console.log();
    console.log(chalk.bold("  CAI would modify:"));
    console.log(dim(result.mcpSkipped && result.hooksSkipped && result.learnSkipped ? "    none outside .cai/" : "    .claude/settings.json"));
    console.log();
    console.log(chalk.bold("  CAI would register/install:"));
    console.log(dim(result.mcpSkipped ? "    MCP server: skipped" : "    MCP server -> cai mcp start"));
    console.log(dim(result.hooksSkipped ? "    Claude Code hooks: skipped" : "    Claude Code hooks -> PreCompact, PreToolUse, Stop"));
    console.log(dim(result.learnSkipped ? "    Correction recording: skipped" : "    Correction recording -> UserPromptSubmit hook"));
    console.log();
    console.log(dim("  Re-run without --dry-run to apply this plan."));
    console.log();
    return;
  }

  // Rules + MCP status
  if (result.rulesGenerated && result.rulesGenerated > 0) {
    console.log(chalk.green(`  ✓ ${result.rulesGenerated} path-scoped rules`) + dim(" — Claude loads context only when touching matching files"));
  } else if (result.rulesSkipped) {
    console.log(dim("  · Rules skipped"));
  }
  if (result.mcpStatus === "registered") {
    console.log(chalk.green(`  ✓ MCP server registered`) + dim(" — Claude can query context on-demand"));
  } else if (result.mcpStatus === "already_registered") {
    console.log(chalk.green(`  ✓ MCP server active`));
  } else if (result.mcpStatus === "claude_not_found") {
    console.log(dim(`  ℹ MCP: run ${chalk.white("cai mcp install")} to connect Claude once installed`));
  } else if (result.mcpSkipped) {
    console.log(dim("  · MCP registration skipped"));
  }
  if (result.skillsGenerated && result.skillsGenerated > 0) {
    console.log(chalk.green(`  ✓ ${result.skillsGenerated} skill${result.skillsGenerated !== 1 ? "s" : ""} generated`) + dim(" — use /cai-check and /cai-sync in Claude Code"));
  } else if (result.skillsSkipped) {
    console.log(dim("  · Claude skills skipped"));
  }
  if (result.hooksInstalled && result.hooksInstalled > 0) {
    console.log(
      chalk.green(`  ✓ ${result.hooksInstalled} Claude Code hook${result.hooksInstalled !== 1 ? "s" : ""} installed`) +
      dim(" — PreCompact, PreToolUse safety, Stop verify")
    );
  } else if (result.hooksSkipped) {
    console.log(dim("  · Claude Code hooks skipped"));
  }
  if (result.learnEnabled) {
    console.log(
      chalk.green(`  ✓ Correction recording enabled`) +
      dim(" — local-only, gitignored, run cai learn forget to wipe")
    );
  } else if (result.learnSkipped) {
    console.log(dim("  · Correction recording skipped"));
  }
  console.log();

  if (result.detectedSubRoot) {
    console.log();
    console.log(chalk.yellow(`  ⚠ Monorepo layout detected`));
    console.log(dim(`  Your project root appears to be in ./${result.detectedSubRoot}/, not the repo root.`));
    console.log(dim(`  All file paths in scaffold files must be prefixed with "${result.detectedSubRoot}/"`));
    console.log(dim(`  to resolve correctly from .cai/'s parent directory.`));
    console.log();
    console.log(dim(`  To configure this automatically, add to .cai/config.json:`));
    console.log(`  ${chalk.white(`{ "projectSubRoot": "${result.detectedSubRoot}" }`)}`);
  }

  if (result.setupRan) {
    console.log(dim("  Run cai check to verify everything is in order."));
  } else {
    console.log(dim("  Open .cai/SETUP.md for next steps, or run .cai/setup.sh to populate context."));
  }
  console.log();
}

export function runBootstrappedSetup(projectRoot: string, args: string[] = []): void {
  const setupScript = join(projectRoot, ".cai", "setup.sh");
  if (!existsSync(setupScript)) {
    throw new Error(`No setup script found at ${setupScript}`);
  }

  const result = spawnSync("bash", [setupScript, ...args], {
    cwd: projectRoot,
    stdio: "inherit",
  });

  if (result.error) {
    throw result.error;
  }

  // 130 = SIGINT (Ctrl+C), 143 = SIGTERM — user cancelled, not an error
  if (typeof result.status === "number" && result.status !== 0 && result.status !== 130 && result.status !== 143) {
    throw new Error(`.cai/setup.sh exited with code ${result.status}`);
  }
}

/** Strip template guidance comments from all markdown files in the scaffold.
 *  Preserves CAI merge markers (<!-- cai:start/end -->) and comments inside code blocks. */
function stripTemplateComments(scaffoldDir: string): void {
  const dirs = ["", "context", "patterns"];
  for (const sub of dirs) {
    const dir = sub ? join(scaffoldDir, sub) : scaffoldDir;
    if (!existsSync(dir)) continue;
    for (const entry of readdirSync(dir)) {
      if (!entry.endsWith(".md")) continue;
      const filePath = join(dir, entry);
      const content = readFileSync(filePath, "utf8");
      const stripped = stripCommentsPreservingMarkers(content);
      if (stripped !== content) {
        writeFileSync(filePath, stripped, "utf8");
      }
    }
  }
}

function stripCommentsPreservingMarkers(content: string): string {
  // Process line by line to skip code blocks
  const lines = content.split("\n");
  const out: string[] = [];
  let inCodeBlock = false;
  let inComment = false;

  for (const line of lines) {
    if (line.startsWith("```")) {
      inCodeBlock = !inCodeBlock;
      out.push(line);
      continue;
    }
    if (inCodeBlock) {
      out.push(line);
      continue;
    }

    // Preserve CAI merge markers
    if (line.includes("<!-- cai:")) {
      out.push(line);
      continue;
    }

    // Track multiline comments
    if (inComment) {
      if (line.includes("-->")) {
        inComment = false;
        // Drop the closing line (part of the template comment)
      }
      continue;
    }

    if (line.trimStart().startsWith("<!--")) {
      if (line.includes("-->")) {
        // Single-line comment — drop it
        continue;
      }
      // Start of multiline comment
      inComment = true;
      continue;
    }

    out.push(line);
  }

  // Clean up excessive blank lines left by removed comments
  return out.join("\n").replace(/\n{3,}/g, "\n\n");
}

/** Generate .claude/skills/ for common CAI workflows.
 *  Skills are Claude Code's slash-command system — users invoke them with /skill-name. */
function generateSkills(projectRoot: string): number {
  const skillsDir = join(projectRoot, ".claude", "skills");
  let count = 0;

  const skills: Array<{ name: string; content: string }> = [
    {
      name: "cai-check",
      content: `---
description: "Run CAI drift detection to verify scaffold docs match the codebase."
---

Run \`cai check\` in the project root and report the results. If the score is below 80, suggest running \`cai fix\` for safe auto-repairs.

If errors remain after fix, list the top 3 issues and suggest what to update.
`,
    },
    {
      name: "cai-sync",
      content: `---
description: "Fix all drift issues — safe auto-fixes first, then AI-assisted updates."
---

1. Run \`cai fix\` to apply safe deterministic repairs.
2. Check results with \`cai check --quiet\`.
3. If issues remain, read each affected .cai/ file and fix the drift issues directly.
4. After fixing, run \`cai check\` again to verify score is 100.
5. Do not modify source code files — only update .cai/ scaffold documentation.
`,
    },
  ];

  for (const skill of skills) {
    try {
      const dir = join(skillsDir, skill.name);
      const filePath = join(dir, "SKILL.md");
      if (existsSync(filePath)) continue;
      mkdirSync(dir, { recursive: true });
      writeFileSync(filePath, skill.content, "utf8");
      count++;
    } catch {
      // Non-fatal: skills are optional convenience features
    }
  }

  return count;
}

function getPackageRoot(): string {
  return dirname(dirname(fileURLToPath(import.meta.url)));
}

// Exported for downstream callers that need to guard against bootstrapping
// CAI into its own package root (e.g. synthesize-scaffold.ts Win #1 wrapper).
export { getPackageRoot };

/**
 * Detect if the actual project root is in a subdirectory (monorepo layout).
 * Returns the relative sub-path if a manifest is found in a direct child dir
 * but NOT in the repo root itself.
 */
function detectSubProjectRoot(projectRoot: string): string | undefined {
  // If a manifest exists at the root, no sub-root needed
  for (const manifest of MANIFEST_FILES) {
    if (existsSync(join(projectRoot, manifest))) return undefined;
  }

  // Check one level down for a manifest
  let entries: string[];
  try {
    entries = readdirSync(projectRoot, { withFileTypes: true })
      .filter((e) => e.isDirectory() && !e.name.startsWith("."))
      .map((e) => e.name);
  } catch {
    return undefined;
  }

  for (const subdir of entries) {
    for (const manifest of MANIFEST_FILES) {
      if (existsSync(join(projectRoot, subdir, manifest))) {
        return subdir;
      }
    }
  }

  return undefined;
}
